import {
  SALES_STATUS_LABEL,
  formatMoney,
  type Page,
  type SalesOrder,
  type SalesStatus,
} from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useCan } from "@/app/me";
import { EmptyState, ErrorState } from "@/components/patterns";
import { Badge, Button, Card, PageTitle, Select, Spinner, Table } from "@/components/ui";
import { apiFetch, errorMessage } from "@/lib/api";
import { Pager, SearchInput } from "../cadastros/widgets";
import { dateBR } from "../estoque/lib";
import { OrderEditor } from "./PedidoEditor";
import { ORDER_KEY } from "./lib";
import { SalesStatusBadge } from "./pickers";

export function PedidosPage() {
  const can = useCan();
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [status, setStatus] = useState<SalesStatus | "todos">("todos");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const params = new URLSearchParams({ status, limit: "50", offset: String(offset) });
  if (q.trim()) params.set("q", q.trim());
  const list = useQuery({
    queryKey: [...ORDER_KEY, params.toString()],
    queryFn: () => apiFetch<Page<SalesOrder>>(`/vendas/pedidos?${params.toString()}`),
    placeholderData: (p) => p,
  });
  const opened = (id: string) => {
    setCreating(false);
    setOpen(id);
  };

  return (
    <div className="space-y-6">
      <PageTitle
        title="Pedidos de venda"
        description="Pedidos com os preços da tabela do cliente. Aprovado, reserva o estoque; confirmado, a mercadoria sai (lote que vence primeiro) e o valor vai para Contas a receber ou fica como recebido à vista. Confirmada, a venda só se corrige por estorno."
        actions={
          can("vendas.pedido.criar") && (
            <Button onClick={() => setCreating(true)} disabled={creating}>
              Novo pedido
            </Button>
          )
        }
      />
      {creating && <OrderEditor id={null} onClose={() => setCreating(false)} onCreated={opened} />}
      {open && <OrderEditor key={open} id={open} onClose={() => setOpen(null)} />}
      <Card>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SearchInput
            placeholder="Buscar número, cliente ou vendedor"
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
          />
          <Select
            aria-label="Situação"
            className="w-56"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as SalesStatus | "todos");
              setOffset(0);
            }}
          >
            <option value="todos">Todos</option>
            {(Object.keys(SALES_STATUS_LABEL) as SalesStatus[]).map((s) => (
              <option key={s} value={s}>
                {SALES_STATUS_LABEL[s]}
              </option>
            ))}
          </Select>
        </div>
        {list.error ? (
          <ErrorState message={errorMessage(list.error)} />
        ) : list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table
              head={["Número", "Data", "Cliente", "Vendedor", "Pagamento", "Total", "Situação", ""]}
            >
              {list.data?.items.map((o) => (
                <tr key={o.id}>
                  <td className="px-3 py-2 font-mono">
                    {o.number}
                    {o.origin === "venda_direta" && (
                      <span className="ml-2 font-sans">
                        <Badge>Venda Direta</Badge>
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">{dateBR(o.orderDate)}</td>
                  <td className="px-3 py-2">{o.customerName}</td>
                  <td className="px-3 py-2">{o.salespersonName ?? "—"}</td>
                  <td className="px-3 py-2">{o.paymentMethodName ?? "—"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {formatMoney(o.totalAmount)}
                  </td>
                  <td className="px-3 py-2">
                    <SalesStatusBadge status={o.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => opened(o.id)}>
                      Abrir
                    </Button>
                  </td>
                </tr>
              ))}
            </Table>
            {list.data?.items.length === 0 && (
              <div className="mt-4">
                <EmptyState
                  title="Nenhum pedido encontrado."
                  description={
                    q.trim() || status !== "todos"
                      ? "Mude a pesquisa ou a situação para ver outros pedidos."
                      : "Os pedidos de venda aparecem aqui assim que forem criados."
                  }
                  {...(can("vendas.pedido.criar") && !q.trim() && status === "todos"
                    ? {
                        action: (
                          <Button onClick={() => setCreating(true)} disabled={creating}>
                            Criar o primeiro pedido
                          </Button>
                        ),
                      }
                    : {})}
                />
              </div>
            )}
            <Pager total={list.data?.total ?? 0} offset={offset} onOffset={setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}
